import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { TertiaryButton, TertiaryButtonGroup, BasicTable, BasicTableHeadersProps } from '@dev/ee-components';
import { safelyEncodeBase64Url } from '@dev/ee-tools';
import { AccessLevel, CampaignStatus, CampaignType } from '@dev/ee-api/ee-api-models';
import { Skeleton } from '@material-ui/lab';
import { noop, truncate, includes } from 'lodash-es';
import { push as Push } from 'redux-first-history';

import useMobile from '@/services/hooks/useMobile';
import { getAccountAccessLevel } from '@/store/account/actions';
import { getConfigStore } from '@/store/config/actions';
import { getCampaignsStore } from '@/store/campaigns/actions';
import { CampaignsPaths, pathsParams } from '@/services/dictionaries/Paths';
import { parseLegacyUrl } from '@/services/tools/parseLegacyUrl';
import { getCampaignCreatorType } from '@/services/tools/getCampaignCreatorType';
import { useQueryParams } from '../hooks/useQueryParams';

const skeletonRow = {
    name: <Skeleton />,
    options: <Skeleton />,
};

export const TableComponent = (): JSX.Element => {
    const dispatch = useDispatch();
    const isMobile = useMobile();
    const { t } = useTranslation();
    const AccountAccessLevel = useSelector(getAccountAccessLevel);
    const config = useSelector(getConfigStore);
    const campaigns = useSelector(getCampaignsStore);
    const { perPage } = useQueryParams();
    const canViewReports = AccountAccessLevel.includes(AccessLevel.ViewReports);

    const headers: BasicTableHeadersProps[] = [
        {
            id: 'name',
            label: '',
        },
        {
            id: 'options',
            label: '',
            columnAlign: isMobile ? 'left' : 'right',
        },
    ];
    const skeletonArray = useMemo(() => Array(perPage).fill(skeletonRow), [perPage]);

    const goToViewDetails = (name: string, status: CampaignStatus, campaignType: CampaignType) => {
        if (config.isStandalone) {
            const editableStatuses = [CampaignStatus.Draft, CampaignStatus.Active, CampaignStatus.Paused];
            const isCampaignEditable = includes(editableStatuses, status);
            const campaignCreatorType = getCampaignCreatorType(campaignType);
            const path = isCampaignEditable
                ? CampaignsPaths.CampaignCreatorName
                : CampaignsPaths.CampaignCreatorDetails;
            dispatch(
                Push(
                    path
                        .replace(pathsParams.type, campaignCreatorType)
                        .replace(pathsParams.name, safelyEncodeBase64Url(name))
                )
            );
        } else {
            dispatch(
                Push(
                    parseLegacyUrl(
                        CampaignsPaths.ViewCampaign.replace(':name', safelyEncodeBase64Url(name)),
                        config.isStandalone
                    )
                )
            );
        }
    };

    const goToViewActivity = (name: string, type: CampaignType) => {
        dispatch(
            Push(CampaignsPaths.CampaignActivity.replace(':type', type).replace(':name', safelyEncodeBase64Url(name)))
        );
    };

    const rows = !campaigns?.byTarget
        ? []
        : campaigns.byTarget.map((campaign): { name: string; options: JSX.Element } => ({
              name: truncate(campaign.Name, { length: 100 }),
              options: (
                  <TertiaryButtonGroup>
                      <TertiaryButton
                          id="view-details-button"
                          onClick={(): void => goToViewDetails(campaign.Name, campaign.Status, campaign.Type)}
                          text={t('viewdetails')}
                      />
                      {canViewReports && (
                          <TertiaryButton
                              id="view-activity-button"
                              onClick={(): void => goToViewActivity(campaign.Name, campaign.Type)}
                              text={t('viewactivity')}
                          />
                      )}
                  </TertiaryButtonGroup>
              ),
          }));

    return (
        <BasicTable
            isEmpty={!campaigns?.loading && !campaigns?.byTarget?.length}
            rows={campaigns?.loading ? skeletonArray : rows}
            headers={headers}
            stickyHeader={true}
            onClickRow={noop}
            emptyStateProps={{
                text: t('emptystate'),
            }}
        />
    );
};
